import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, LoginCredentials, AuthResponse } from '../types/auth'
import { authService } from '../services/authService'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasFieldPermission: (field: string) => boolean
  canOverrideAccess: () => boolean
  hasRole: (role: User['role']) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on mount
    const currentUser = authService.getCurrentUser()
    setUser(currentUser)
    setIsLoading(false)
  }, [])

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true)
    try {
      const response: AuthResponse = await authService.login(credentials)
      setUser(response.user)
    } catch (error) {
      setIsLoading(false)
      throw error
    }
    setIsLoading(false)
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
    // Note: Navigation is handled by ProtectedRoute redirect or explicit navigation
  }

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission)
  }

  const hasFieldPermission = (field: string): boolean => {
    return authService.hasFieldPermission(field)
  }

  const canOverrideAccess = (): boolean => {
    return authService.canOverrideAccess()
  }

  const hasRole = (role: User['role']): boolean => {
    return authService.hasRole(role)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasPermission,
        hasFieldPermission,
        canOverrideAccess,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

